import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { opsFetch } from "../lib/api";
import { Count, Pill, Thumb } from "../components/ui";
import { ExperiencePreview } from "../components/PreviewPane";
import { SplitPane } from "../components/SplitPane";
import { downloadCsv } from "../lib/exportCsv";

type Exp = {
  id: string;
  slug: string;
  status: string;
  category: string;
  city_slug: string;
  title_en: string;
  title_zh: string;
  blurb_en: string;
  blurb_zh: string;
  image_src: string;
  price_from_cny: number | null;
  affiliate_url: string | null;
};

type ListRes = { data: { items: Exp[]; total: number } };

const EMPTY = {
  slug: "",
  status: "draft",
  category: "sightseeing",
  city_slug: "",
  title_en: "",
  title_zh: "",
  blurb_en: "",
  blurb_zh: "",
  image_src: "",
  price_from_cny: "" as string | number,
  affiliate_url: "",
};

export function ExperiencesPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [qtext, setQtext] = useState("");
  const [editing, setEditing] = useState<typeof EMPTY | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["ops-experiences", status],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: "100" });
      if (status) params.set("status", status);
      return opsFetch<ListRes>(`/api/ops/experiences?${params}`);
    },
  });

  const save = useMutation({
    mutationFn: (body: typeof EMPTY) => {
      const slug = body.slug.trim().toLowerCase();
      const price =
        body.price_from_cny === "" || body.price_from_cny === null
          ? null
          : Number(body.price_from_cny);
      return opsFetch(`/api/ops/experiences/${encodeURIComponent(slug)}`, {
        method: "PUT",
        body: JSON.stringify({
          ...body,
          slug,
          price_from_cny: Number.isFinite(price as number) ? price : null,
          affiliate_url: body.affiliate_url || null,
        }),
      });
    },
    onSuccess: () => {
      setMsg("Saved");
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["ops-experiences"] });
    },
    onError: (e: Error) => setMsg(e.message),
  });

  const archive = useMutation({
    mutationFn: (slug: string) =>
      opsFetch(`/api/ops/experiences/${encodeURIComponent(slug)}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops-experiences"] }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.slug || !editing.title_en) return;
    save.mutate(editing);
  }

  const needle = qtext.trim().toLowerCase();
  const shown = (q.data?.data.items ?? []).filter((item) => {
    if (category && item.category !== category) return false;
    if (!needle) return true;
    return `${item.title_en} ${item.title_zh} ${item.city_slug} ${item.slug}`.toLowerCase().includes(needle);
  });
  const row = shown.find((item) => item.id === picked) ?? shown[0];
  const preview = editing
    ? {
        title: editing.title_en,
        titleZh: editing.title_zh,
        blurb: editing.blurb_en,
        image: editing.image_src,
        city: editing.city_slug,
        category: editing.category,
        kind: "experience",
        layout: "experience" as const,
        price: editing.price_from_cny,
        status: editing.status,
        bookUrl: editing.affiliate_url,
      }
    : {
        title: row?.title_en || "",
        titleZh: row?.title_zh,
        blurb: row?.blurb_en,
        image: row?.image_src,
        city: row?.city_slug,
        category: row?.category,
        kind: "experience",
        layout: "experience" as const,
        price: row?.price_from_cny,
        status: row?.status,
        bookUrl: row?.affiliate_url || "",
      };

  return (
    <SplitPane>
      <div>
      <div className="ops-toolbar">
        <button className="ops-btn ops-btn--primary" onClick={() => setEditing({ ...EMPTY })} type="button">
          New experience
        </button>
        <select className="ops-input" onChange={(e) => setStatus(e.target.value)} value={status}>
          <option value="">All</option>
          <option value="draft">draft</option>
          <option value="publish">publish</option>
          <option value="archived">archived</option>
        </select>
        <select className="ops-input" onChange={(e) => setCategory(e.target.value)} value={category}>
          <option value="">All categories</option>
          {[...new Set((q.data?.data.items ?? []).map((item) => item.category).filter(Boolean))].map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
        <input className="ops-input" onChange={(e) => setQtext(e.target.value)} placeholder="Search title or city" value={qtext} />
        <button
          className="ops-btn"
          onClick={() =>
            downloadCsv(
              "experiences.csv",
              ["slug", "title", "city", "category", "status"],
              shown.map((item) => [item.slug, item.title_en, item.city_slug, item.category, item.status]),
            )
          }
          type="button"
        >
          Export
        </button>
        {q.data ? <Count label="records" n={shown.length} /> : null}
        {msg ? <span className="ops-ok">{msg}</span> : null}
      </div>

      {editing ? (
        <form className="ops-panel ops-form" onSubmit={onSubmit}>
          <div className="ops-form__grid">
            <label>
              <span>Slug</span>
              <input
                className="ops-input"
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                required
                value={editing.slug}
              />
            </label>
            <label>
              <span>Title EN</span>
              <input
                className="ops-input"
                onChange={(e) => setEditing({ ...editing, title_en: e.target.value })}
                required
                value={editing.title_en}
              />
            </label>
            <label>
              <span>Title ZH</span>
              <input
                className="ops-input"
                onChange={(e) => setEditing({ ...editing, title_zh: e.target.value })}
                value={editing.title_zh}
              />
            </label>
            <label>
              <span>City slug</span>
              <input
                className="ops-input"
                onChange={(e) => setEditing({ ...editing, city_slug: e.target.value })}
                value={editing.city_slug}
              />
            </label>
            <label>
              <span>Category</span>
              <input
                className="ops-input"
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                value={editing.category}
              />
            </label>
            <label>
              <span>Price from CNY</span>
              <input
                className="ops-input"
                onChange={(e) => setEditing({ ...editing, price_from_cny: e.target.value })}
                type="number"
                value={editing.price_from_cny}
              />
            </label>
            <label>
              <span>Affiliate URL</span>
              <input
                className="ops-input"
                onChange={(e) => setEditing({ ...editing, affiliate_url: e.target.value })}
                value={editing.affiliate_url}
              />
            </label>
            <label>
              <span>Image URL</span>
              <input
                className="ops-input"
                onChange={(e) => setEditing({ ...editing, image_src: e.target.value })}
                value={editing.image_src}
              />
            </label>
            <label>
              <span>Status</span>
              <select
                className="ops-input"
                onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                value={editing.status}
              >
                <option value="draft">draft</option>
                <option value="publish">publish</option>
                <option value="archived">archived</option>
              </select>
            </label>
          </div>
          <label className="ops-form__full">
            <span>Blurb EN</span>
            <textarea
              className="ops-input ops-textarea"
              onChange={(e) => setEditing({ ...editing, blurb_en: e.target.value })}
              rows={3}
              value={editing.blurb_en}
            />
          </label>
          <div className="ops-toolbar">
            <button className="ops-btn ops-btn--primary" disabled={save.isPending} type="submit">
              Save
            </button>
            <button className="ops-btn ops-btn--ghost" onClick={() => setEditing(null)} type="button">
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {q.isLoading ? <p className="ops-muted">Loading…</p> : null}
      {q.data ? (
        <div className="ops-table-wrap ops-panel">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>City</th>
                <th>Card</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => (
                <tr
                  className={picked === d.id ? "is-selected" : undefined}
                  key={d.id}
                  onClick={() => setPicked(d.id)}
                >
                  <td>
                    <div className="ops-row-main">
                      <Thumb src={d.image_src} />
                      <div className="ops-row-copy">
                        <strong>{d.title_en}</strong>
                        <span className="ops-tags">
                          <Pill value={d.category} />
                          {d.city_slug ? <span className="ops-muted">{d.city_slug}</span> : null}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <code>{d.slug}</code>
                  </td>
                  <td>{d.city_slug || "—"}</td>
                  <td>{d.image_src && d.blurb_en && d.affiliate_url ? <span className="ops-ready">Bookable</span> : <span className="ops-gap">Missing {[!d.image_src && "image", !d.blurb_en && "blurb", !d.affiliate_url && "link"].filter(Boolean).join(", ")}</span>}</td>
                  <td><Pill value={d.status} /></td>
                  <td>
                    <button
                      className="ops-btn ops-btn--ghost"
                      onClick={() =>
                        setEditing({
                          slug: d.slug,
                          status: d.status,
                          category: d.category,
                          city_slug: d.city_slug,
                          title_en: d.title_en,
                          title_zh: d.title_zh,
                          blurb_en: d.blurb_en,
                          blurb_zh: d.blurb_zh,
                          image_src: d.image_src,
                          price_from_cny: d.price_from_cny ?? "",
                          affiliate_url: d.affiliate_url || "",
                        })
                      }
                      type="button"
                    >
                      Edit
                    </button>
                    {d.status !== "archived" ? (
                      <button
                        className="ops-btn ops-btn--ghost"
                        onClick={() => archive.mutate(d.slug)}
                        type="button"
                      >
                        Archive
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      </div>
      <ExperiencePreview item={preview} />
    </SplitPane>
  );
}
